<template>
  <GradientHeader
    :logo="logo"
    :colour="colours.primary"
    fullHeight
  >
    <OffsetColorBox
      :colour="colours.accentPrimary"
      :gray="colours.primaryIsWhite"
      class="w-160 max-w-full mx-auto px-4"
      smallHeading
      boxInnerClass="md:px-10"
    >
      <template slot="heading">
        {{ $t('download.downloadX', { type: 'envelope' }) }}
      </template>

      <IconCircular
        slot="icon"
        class="bg-success !w-10 !h-10"
      >
        <DownloadIcon class="w-3/5 text-white" />
      </IconCircular>

      <p class="text-body px-4 md:px-8">
        {{ $t('download.downloadInAFewSeconds') }}

        <button
          class="text-body bg-transparent border-none text-blue hover:text-blueHover underline p-0 cursor-pointer"
          @click="handleDownload"
        >
          {{ $t('download.ifDoesntStart') }}
        </button>
      </p>

      <div
        v-if="showProgress"
        class="mt-4 px-4 md:px-8"
      >
        <div class="h-2 bg-gray-200 overflow-hidden">
          <div
            class="h-full bg-blue rounded-normal transition-all duration-300 ease-out"
            :style="{ width: `${progress}%` }"
          />
        </div>
        <p class="text-sm text-gray-600 mt-2">
          {{ $t('download.downloadingYourX', { type: 'envelope' }) }} {{ Math.round(progress) }}%
        </p>
      </div>
    </OffsetColorBox>
  </GradientHeader>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useActions, useGetters } from 'vuex-composition-helpers/dist'
import { useTimeoutFn } from '@vueuse/core'

import GradientHeader from '@/components/library/GradientHeader.vue'
import OffsetColorBox from '@/components/library/OffsetColorBox.vue'
import IconCircular from '@/components/icons/IconCircular.vue'
import DownloadIcon from '@/components/icons/DownloadIcon.vue'

const { branding } = useGetters(['branding'])

const { downloadEnvelope } = useActions(['downloadEnvelope'])

const isDownloading = ref(false)
const progress = ref(null)
const showProgress = ref(false)
const {
  isPending: waitingToShowProgress,
  start: waitToShowProgress,
  stop: stopWaitingToShowProgress
} = useTimeoutFn(
  () => { showProgress.value = true },
  3000,
  { immediate: false }
)

async function handleDownload () {
  if (isDownloading.value) {
    return
  }

  isDownloading.value = true
  progress.value = 0
  showProgress.value = false

  downloadEnvelope(
    (event) => {
      progress.value = Math.round(Math.min(event.loaded / event.total, 1) * 100)

      if (!waitingToShowProgress.value) {
        waitToShowProgress()
      }
    })
    .finally(() => {
      isDownloading.value = false
      stopWaitingToShowProgress()
    })
}

const colours = computed(() => branding.value ? branding.value.colours : { accentSecondary: '#ffffff' })
const logo = computed(() => branding.value && branding.value.logo)

onMounted(() => {
  handleDownload()
})
</script>
